import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { mrbAPI, filesAPI, qualityNcrAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { MrbPhotoGallery } from '@/pages/quality/ncr/MrbPhotoGallery';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Clock, CheckCircle2, AlertTriangle, Shield,
  ChevronLeft, ChevronRight, Loader2, FileUp, Download,
  RotateCcw, ArrowRightLeft, Ban, UserCheck, ClipboardCheck,
  FileText, XCircle, Mail, Package, MessageSquare, Paperclip,
  Image, Trash2, Plus, Pencil, Check, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  waiting_customer_disposition: 'bg-purple-50 text-purple-700 border-purple-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const DISPOSITION_STYLES = {
  pending: 'bg-gray-50 text-gray-600 border-gray-200',
  reaccept: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rework: 'bg-orange-50 text-orange-700 border-orange-200',
  reject: 'bg-red-50 text-red-700 border-red-200',
};

const ROLE_LABELS = {
  program_mgmt: 'Program Management',
  quality: 'Quality',
  inventory: 'Inventory',
};

export default function MRBPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({});
  const limit = 25;

  // Case detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loadingCase, setLoadingCase] = useState(false);

  // Disposition dialog
  const [dispOpen, setDispOpen] = useState(false);
  const [dispType, setDispType] = useState('');
  const [dispRemarks, setDispRemarks] = useState('');
  const [dispQty, setDispQty] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Approval dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveRole, setApproveRole] = useState('');
  const [approveRemarks, setApproveRemarks] = useState('');

  // File upload
  const [uploading, setUploading] = useState(false);

  // Send to Customer dialog
  const [sendToCustomerOpen, setSendToCustomerOpen] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCc, setCustomerCc] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [sendingToCustomer, setSendingToCustomer] = useState(false);

  // Customer Disposition dialog
  const [custDispOpen, setCustDispOpen] = useState(false);
  const [custDispType, setCustDispType] = useState('');
  const [custDispRemarks, setCustDispRemarks] = useState('');
  const [custDispFile, setCustDispFile] = useState(null);
  const [recordingCustDisp, setRecordingCustDisp] = useState(false);

  // Notes
  const [newNote, setNewNote] = useState('');
  const [newNoteType, setNewNoteType] = useState('general');
  const [addingNote, setAddingNote] = useState(false);

  // Attachments
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentDesc, setAttachmentDesc] = useState('');
  const [attachmentType, setAttachmentType] = useState('document');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Include attachments in email
  const [includeAttachments, setIncludeAttachments] = useState(true);

  // Inline edit Customer WO#
  const [editingWO, setEditingWO] = useState(false);
  const [editWOValue, setEditWOValue] = useState('');
  const [savingWO, setSavingWO] = useState(false);

  // Admin: Repair Availability dialog
  const isAdmin = user?.role === 'admin';
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairSearch, setRepairSearch] = useState('');
  const [repairChallans, setRepairChallans] = useState([]);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairChallanId, setRepairChallanId] = useState('');
  const [repairLineIndex, setRepairLineIndex] = useState('');
  const [repairMode, setRepairMode] = useState('add');
  const [repairQty, setRepairQty] = useState('');
  const [repairRemarks, setRepairRemarks] = useState('');
  const [repairSubmitting, setRepairSubmitting] = useState(false);

  const selectedRepairChallan = repairChallans.find(c => c.id === repairChallanId);
  const selectedRepairLine = selectedRepairChallan && repairLineIndex !== ''
    ? selectedRepairChallan.items[Number(repairLineIndex)] : null;

  const loadRepairChallans = async () => {
    setRepairLoading(true);
    try {
      const res = await mrbAPI.repairSearchChallans(repairSearch.trim());
      setRepairChallans(res.data.challans || []);
    } catch {
      toast.error('Failed to load inbound challans');
    } finally {
      setRepairLoading(false);
    }
  };

  const openRepairDialog = () => {
    setRepairOpen(true);
    setRepairSearch('');
    setRepairChallanId('');
    setRepairLineIndex('');
    setRepairMode('add');
    setRepairQty('');
    setRepairRemarks('');
    setRepairChallans([]);
    loadRepairChallans();
  };

  const handleRepairSubmit = async () => {
    if (!repairChallanId || repairLineIndex === '') {
      toast.error('Select a challan and line item');
      return;
    }
    const qty = parseFloat(repairQty);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity greater than 0');
      return;
    }
    setRepairSubmitting(true);
    try {
      const res = await mrbAPI.repairChallanOkQty({
        challan_id: repairChallanId,
        line_index: Number(repairLineIndex),
        quantity: qty,
        mode: repairMode,
        remarks: repairRemarks.trim(),
      });
      toast.success(res.data.message);
      setRepairOpen(false);
      loadStats();
      loadCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to repair availability');
    } finally {
      setRepairSubmitting(false);
    }
  };

  const handleSaveWO = async () => {
    if (!selectedCase) return;
    setSavingWO(true);
    try {
      await mrbAPI.updateCustomerWO(selectedCase.id, { customer_wo_number: editWOValue });
      toast.success('Customer WO# updated');
      setEditingWO(false);
      await refreshCaseDetail(selectedCase.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update WO#');
    } finally { setSavingWO(false); }
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await mrbAPI.dashboard();
      setStats(res.data);
    } catch { /* silent */ }
  }, []);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (activeTab === 'active') params.status = 'active';
      else if (activeTab === 'closed') params.status = 'closed';
      const res = await mrbAPI.listCases(params);
      setCases(res.data.cases || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load MRB cases'); }
    finally { setLoading(false); }
  }, [page, limit, search, activeTab]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCases(); }, [loadCases]);
  useEffect(() => {
    const caseId = new URLSearchParams(window.location.search).get('case');
    if (caseId) openCaseDetail({ id: caseId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCaseDetail = async (caseItem) => {
    setLoadingCase(true);
    setDetailOpen(true);
    try {
      const res = await mrbAPI.getCase(caseItem.id);
      const caseData = res.data;
      setSelectedCase(caseData);
      // Auto-prompt email dialog for customer material cases not yet emailed
      if (
        caseData.is_customer_material &&
        !caseData.customer_notification_date &&
        caseData.status !== 'closed'
      ) {
        // Small delay so the detail dialog renders first
        setTimeout(() => {
          const ccParts = [];
          const senderEmail = caseData.current_user_email || user?.email || '';
          if (senderEmail) ccParts.push(senderEmail);
          ccParts.push('toolroom@bhpl.co.in', 'ae@bhpl.co.in');
          setCustomerEmail(caseData.customer_email_from_master || '');
          setCustomerCc(ccParts.join(', '));
          setCustomerMessage('');
          setIncludeAttachments(true);
          setSendToCustomerOpen(true);
        }, 400);
      }
    } catch {
      toast.error('Failed to load case details');
      setDetailOpen(false);
    } finally { setLoadingCase(false); }
  };

  const refreshCaseDetail = async (caseId) => {
    try {
      const res = await mrbAPI.getCase(caseId);
      setSelectedCase(res.data);
    } catch { /* silent */ }
  };

  // Disposition actions
  const openDisposition = (type) => {
    setDispType(type);
    setDispRemarks('');
    setDispQty(String(selectedCase?.quantity || ''));
    setDispOpen(true);
  };

  const handleDisposition = async () => {
    if (!selectedCase) return;
    setSubmitting(true);
    try {
      const res = await mrbAPI.setDisposition(selectedCase.id, {
        disposition: dispType,
        quantity: parseFloat(dispQty) || selectedCase.quantity,
        remarks: dispRemarks,
      });
      toast.success(res.data.message);
      const isInternalReject = dispType === 'reject' && selectedCase.source === 'production_quarantine';
      setDispOpen(false);
      if (isInternalReject) {
        const caseId = selectedCase.id;
        setDetailOpen(false);
        loadCases();
        loadStats();
        toast('Recorded in the Internal NC Register', {
          description: 'Fill the closure actions for this rejection.',
          action: { label: 'Fill closure', onClick: () => qualityNcrAPI.internalByMrb(caseId).then((r) => { window.location.href = `/quality/internal-ncr/${r.data.id}`; }).catch(() => { window.location.href = '/quality/internal-ncr'; }) },
          duration: 10000,
        });
        return;
      }
      await refreshCaseDetail(selectedCase.id);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set disposition');
    } finally { setSubmitting(false); }
  };

  // Approval actions
  const openApproval = () => {
    setApproveRole('');
    setApproveRemarks('');
    setApproveOpen(true);
  };

  const handleApproval = async () => {
    if (!selectedCase || !approveRole) return;
    setSubmitting(true);
    try {
      const res = await mrbAPI.approveReaccept(selectedCase.id, {
        approval_role: approveRole,
        remarks: approveRemarks,
      });
      toast.success(res.data.message);
      setApproveOpen(false);
      await refreshCaseDetail(selectedCase.id);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally { setSubmitting(false); }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCase) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mrbAPI.uploadCustomerAcceptance(selectedCase.id, formData);
      toast.success(res.data.message || 'File uploaded');
      await refreshCaseDetail(selectedCase.id);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadFile = async () => {
    if (!selectedCase?.customer_acceptance_file_id) return;
    try {
      const res = await mrbAPI.downloadCustomerAcceptance(selectedCase.id);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedCase.customer_acceptance_filename || 'customer_acceptance.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  // Send to Customer
  const openSendToCustomer = () => {
    // Auto-populate customer email from master, fall back to previously used email
    setCustomerEmail(selectedCase?.customer_email_from_master || selectedCase?.customer_notification_email || '');
    // Auto-populate CC with sender's email + fixed BHPL addresses
    const ccParts = [];
    const senderEmail = selectedCase?.current_user_email || user?.email || '';
    if (senderEmail) ccParts.push(senderEmail);
    ccParts.push('toolroom@bhpl.co.in', 'ae@bhpl.co.in');
    setCustomerCc(ccParts.join(', '));
    setCustomerMessage('');
    setSendToCustomerOpen(true);
  };

  const handleSendToCustomer = async () => {
    if (!customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSendingToCustomer(true);
    try {
      const res = await mrbAPI.sendToCustomer(selectedCase.id, {
        customer_email: customerEmail.trim(),
        cc_emails: customerCc.trim(),
        additional_message: customerMessage.trim(),
        include_attachments: includeAttachments,
      });
      toast.success(res.data.message);
      if (!res.data.email_sent) {
        toast.warning('Email was not sent - check Resend configuration');
      }
      setSendToCustomerOpen(false);
      await refreshCaseDetail(selectedCase.id);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send to customer');
    } finally { setSendingToCustomer(false); }
  };

  // Customer Disposition
  const openCustomerDisposition = () => {
    setCustDispType('');
    setCustDispRemarks('');
    setCustDispFile(null);
    setCustDispOpen(true);
  };

  const handleRecordCustomerDisposition = async () => {
    if (!custDispType) {
      toast.error('Please select customer disposition');
      return;
    }
    setRecordingCustDisp(true);
    try {
      const formData = new FormData();
      formData.append('disposition_type', custDispType);
      formData.append('remarks', custDispRemarks);
      if (custDispFile) {
        formData.append('file', custDispFile);
      }
      const res = await mrbAPI.recordCustomerDisposition(selectedCase.id, formData);
      toast.success(res.data.message);
      setCustDispOpen(false);
      await refreshCaseDetail(selectedCase.id);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record disposition');
    } finally { setRecordingCustDisp(false); }
  };

  const handleDownloadDispositionFile = async () => {
    if (!selectedCase?.customer_disposition_file_id) return;
    try {
      const res = await mrbAPI.downloadCustomerDispositionFile(selectedCase.id);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedCase.customer_disposition_filename || 'customer_disposition.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setAddingNote(true);
    try {
      await mrbAPI.addNote(selectedCase.id, { note: newNote.trim(), note_type: newNoteType });
      toast.success('Note added');
      setNewNote('');
      setNewNoteType('general');
      await refreshCaseDetail(selectedCase.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await mrbAPI.deleteNote(selectedCase.id, noteId);
      toast.success('Note deleted');
      await refreshCaseDetail(selectedCase.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  // Attachments handlers
  const handleUploadAttachment = async () => {
    if (!attachmentFile) {
      toast.error('Please select a file');
      return;
    }
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', attachmentFile);
      formData.append('description', attachmentDesc);
      formData.append('attachment_type', attachmentType);
      const res = await mrbAPI.uploadAttachment(selectedCase.id, formData);
      toast.success(res.data.message);
      setAttachmentFile(null);
      setAttachmentDesc('');
      setAttachmentType('document');
      await refreshCaseDetail(selectedCase.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload attachment');
    } finally { setUploadingAttachment(false); }
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const res = await mrbAPI.downloadAttachment(selectedCase.id, fileId);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  const handleDeleteAttachment = async (fileId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await mrbAPI.deleteAttachment(selectedCase.id, fileId);
      toast.success('Attachment deleted');
      await refreshCaseDetail(selectedCase.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const totalPages = Math.ceil(total / limit);
  const userRoles = selectedCase?.current_user_mrb_roles || [];
  const isAuthorized = userRoles.length > 0;
  const caseIsActive = selectedCase && selectedCase.status !== 'closed';
  const isReacceptReview = selectedCase?.disposition === 'reaccept';

  // Available approval roles for this user that haven't been used yet
  const availableApprovalRoles = userRoles.filter(role => {
    const approval = selectedCase?.approvals?.[role];
    return !approval?.approved;
  });

  // Check if current user already approved (under any role)
  const userAlreadyApproved = selectedCase?.approvals
    ? Object.values(selectedCase.approvals).some(a => a.approved && a.user_id === selectedCase?.current_user_mrb_roles?._userId)
    : false;

  return (
    <div className="space-y-4" data-testid="mrb-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Material Review Board</h1>
          <p className="text-xs text-muted-foreground">Review quarantined and rejected material for disposition</p>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={openRepairDialog}
            data-testid="mrb-repair-availability-btn"
          >
            <Wrench className="w-3.5 h-3.5 mr-1.5" /> Repair Availability
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <div>
                <p className="text-lg font-bold text-amber-700" data-testid="stat-open">{stats.open_cases || 0}</p>
                <p className="text-[10px] text-amber-600">Open Cases</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-lg font-bold text-blue-700" data-testid="stat-in-review">{stats.in_review || 0}</p>
                <p className="text-[10px] text-blue-600">In Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-lg font-bold text-emerald-700" data-testid="stat-closed">{stats.closed || 0}</p>
                <p className="text-[10px] text-emerald-600">Closed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-orange-600" />
              <div>
                <p className="text-lg font-bold text-orange-700" data-testid="stat-reaccept">{stats.pending_reaccept || 0}</p>
                <p className="text-[10px] text-orange-600">Pending Re-accept</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); setPage(1); }}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Active ({(stats.open_cases || 0) + (stats.in_review || 0)})
          </TabsTrigger>
          <TabsTrigger value="closed" data-testid="tab-closed">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Closed
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4 space-y-3">
          <Card>
            <CardContent className="p-3">
              <div className="relative max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search challan, item, supplier..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  className="h-8 text-xs pl-7"
                  data-testid="mrb-search"
                />
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <Card><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>
          ) : cases.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'active' ? 'No active MRB cases' : 'No closed MRB cases'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Table header */}
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Item</th>
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Challan</th>
                        <th className="text-left p-2.5 font-medium text-muted-foreground hidden md:table-cell">Cust WO#</th>
                        <th className="text-center p-2.5 font-medium text-muted-foreground">Qty</th>
                        <th className="text-center p-2.5 font-medium text-muted-foreground">Source</th>
                        <th className="text-center p-2.5 font-medium text-muted-foreground">Location</th>
                        <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                        <th className="text-center p-2.5 font-medium text-muted-foreground">Disposition</th>
                        <th className="text-right p-2.5 font-medium text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`mrb-case-row-${c.id}`}>
                          <td className="p-2.5">
                            <p className="font-medium"><EntityLink type="item" id={c.item_id}>{c.item_name}</EntityLink></p>
                            <p className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={c.item_id} className="font-mono text-[10px] text-muted-foreground">{c.item_unique_id}</EntityLink></p>
                            {c.ncr_number && (
                              <p className="text-[10px] font-mono text-violet-700" data-testid={`mrb-ncr-number-${c.id}`}>{c.ncr_number}</p>
                            )}
                          </td>
                          <td className="p-2.5">
                            <p className="font-medium">{c.challan_number}</p>
                            <p className="text-[10px] text-muted-foreground">{c.supplier_name}</p>
                          </td>
                          <td className="p-2.5 hidden md:table-cell">
                            <p className="text-xs font-mono text-muted-foreground">{c.customer_wo_number || '-'}</p>
                          </td>
                          <td className="p-2.5 text-center font-medium">{c.quantity}</td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={`text-[10px] ${c.source === 'qc_quarantine' ? 'text-orange-600 border-orange-200' : 'text-red-600 border-red-200'}`}>
                              {c.source === 'qc_quarantine' ? 'Quarantine' : 'Rejected'}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-center text-[10px]">{c.godown_name}</td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[c.status] || ''}`}>
                              {c.status === 'in_review' ? 'In Review' : 
                               c.status === 'waiting_customer_disposition' ? 'Waiting Customer' : 
                               c.status}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={`text-[10px] ${DISPOSITION_STYLES[c.disposition] || ''}`}>
                              {c.disposition === 'reaccept' ? 'Re-accept' : c.disposition}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => openCaseDetail(c)}
                              data-testid={`mrb-open-case-${c.id}`}
                            >
                              <ClipboardCheck className="w-3 h-3 mr-1" /> Review
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                      <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                      Next <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ===================== CASE DETAIL DIALOG ===================== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {loadingCase ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : selectedCase ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  MRB Case: {selectedCase.item_name}
                </DialogTitle>
                <DialogDescription asChild>
                  <div>
                    Challan: {selectedCase.challan_number} | Supplier: {selectedCase.supplier_name}
                    {selectedCase.is_customer_material && (
                      <Badge variant="outline" className="ml-2 text-[10px] text-purple-600 border-purple-200">Customer Material</Badge>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Case Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-[10px] text-muted-foreground">Item ID</p>
                    <p className="text-xs font-mono font-medium">{selectedCase.item_unique_id}</p>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-[10px] text-muted-foreground">Quantity</p>
                    <p className="text-xs font-medium">{selectedCase.quantity}</p>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-[10px] text-muted-foreground">Source</p>
                    <Badge variant="outline" className={`text-[10px] ${selectedCase.source !== 'qc_reject' ? 'text-orange-600 border-orange-200' : 'text-red-600 border-red-200'}`}>
                      {selectedCase.source === 'qc_quarantine' ? 'QC Quarantine' : selectedCase.source === 'production_quarantine' ? 'Production Quarantine' : 'QC Rejected'}
                    </Badge>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-[10px] text-muted-foreground">Location</p>
                    <p className="text-xs font-medium">{selectedCase.godown_name}</p>
                  </div>
                </div>

                {/* QC Remarks */}
                {selectedCase.qc_remarks && (
                  <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="p-3">
                      <p className="text-[10px] font-medium text-amber-700 mb-1">QC Remarks / Reason for Quarantine</p>
                      <p className="text-xs">{selectedCase.qc_remarks}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Customer Material Details */}
                {selectedCase.is_customer_material && (
                  <Card className="border-purple-200 bg-purple-50/30">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-[10px] font-medium text-purple-700 flex items-center gap-1">
                        <Package className="w-3 h-3" /> Customer Material Details
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <p className="text-[10px] text-muted-foreground">Customer DC#</p>
                          <p className="text-xs font-mono font-medium">{selectedCase.customer_challan_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Customer WO#</p>
                          {editingWO ? (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Input
                                value={editWOValue}
                                onChange={e => setEditWOValue(e.target.value)}
                                className="h-6 text-xs font-mono w-28 px-1"
                                autoFocus
                                data-testid="mrb-edit-wo-input"
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveWO(); if (e.key === 'Escape') setEditingWO(false); }}
                              />
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-emerald-600" onClick={handleSaveWO} disabled={savingWO} data-testid="mrb-save-wo-btn">
                                {savingWO ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground" onClick={() => setEditingWO(false)}>
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <p className="text-xs font-mono font-medium" data-testid="mrb-customer-wo">{selectedCase.customer_wo_number || 'N/A'}</p>
                              <Button
                                variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-purple-600"
                                onClick={() => { setEditWOValue(selectedCase.customer_wo_number || ''); setEditingWO(true); }}
                                data-testid="mrb-edit-wo-btn"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Customer</p>
                          <p className="text-xs font-medium">{selectedCase.supplier_name}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Received Date</p>
                          <p className="text-xs font-medium">{selectedCase.created_at ? new Date(selectedCase.created_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                      
                      {/* Customer Notification Status */}
                      {selectedCase.customer_notification_date && (
                        <div className="mt-2 p-2 bg-white rounded border border-purple-200">
                          <p className="text-[10px] text-purple-700 font-medium">Sent to Customer for Disposition</p>
                          <p className="text-[10px] text-muted-foreground">
                            Email: {selectedCase.customer_notification_email} | 
                            Date: {new Date(selectedCase.customer_notification_date).toLocaleString()}
                          </p>
                        </div>
                      )}
                      
                      {/* Customer Disposition Response */}
                      {selectedCase.customer_disposition_type && (
                        <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-200">
                          <p className="text-[10px] text-emerald-700 font-medium">Customer Disposition Received</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                              {selectedCase.customer_disposition_type === 'use_as_is' ? 'Use As Is' :
                               selectedCase.customer_disposition_type === 'rework' ? 'Rework' :
                               selectedCase.customer_disposition_type === 'return' ? 'Return' : 'Scrap'}
                            </Badge>
                            {selectedCase.customer_disposition_file_id && (
                              <Button size="sm" variant="ghost" className="h-5 text-[10px]" onClick={handleDownloadDispositionFile}>
                                <Download className="w-3 h-3 mr-1" /> {selectedCase.customer_disposition_filename || 'Download'}
                              </Button>
                            )}
                          </div>
                          {selectedCase.customer_disposition_remarks && (
                            <p className="text-[10px] text-muted-foreground mt-1">{selectedCase.customer_disposition_remarks}</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Status & Disposition */}
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`${STATUS_STYLES[selectedCase.status]}`}>
                    Status: {selectedCase.status === 'in_review' ? 'In Review' : 
                             selectedCase.status === 'waiting_customer_disposition' ? 'Waiting Customer Disposition' :
                             selectedCase.status}
                  </Badge>
                  <Badge variant="outline" className={`${DISPOSITION_STYLES[selectedCase.disposition]}`}>
                    Disposition: {selectedCase.disposition === 'reaccept' ? 'Re-accept' : selectedCase.disposition}
                  </Badge>
                </div>

                {/* Send to Customer / Record Disposition Actions */}
                {selectedCase.is_customer_material && caseIsActive && isAuthorized && (
                  <div className="flex gap-2">
                    {!selectedCase.customer_disposition_type && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                        onClick={openSendToCustomer}
                        data-testid="mrb-send-to-customer-btn"
                      >
                        <Mail className="w-3 h-3 mr-1" /> 
                        {selectedCase.customer_notification_date ? 'Resend to Customer' : 'Send to Customer for Disposition'}
                      </Button>
                    )}
                    {selectedCase.status === 'waiting_customer_disposition' && !selectedCase.customer_disposition_type && (
                      <Button 
                        size="sm" 
                        variant="default"
                        className="text-xs"
                        onClick={openCustomerDisposition}
                        data-testid="mrb-record-disposition-btn"
                      >
                        <ClipboardCheck className="w-3 h-3 mr-1" /> Record Customer Disposition
                      </Button>
                    )}
                  </div>
                )}

                {/* =============== CASE NOTES =============== */}
                <Card>
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                      Case Notes / History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 space-y-3">
                    {/* Existing Notes */}
                    {(selectedCase.notes || []).length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCase.notes.map((note) => (
                          <div key={note.id} className="p-2 bg-slate-50 rounded border text-xs">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <span className="font-medium text-blue-700">[{note.note_type?.replace('_', ' ').toUpperCase() || 'GENERAL'}]</span>{' '}
                                <span>{note.note}</span>
                              </div>
                              {(note.created_by === user?.id || user?.role === 'admin') && caseIsActive && (
                                <Button variant="ghost" size="icon" className="h-5 w-5 text-red-500 hover:bg-red-50" onClick={() => handleDeleteNote(note.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              — {note.created_by_name} • {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No notes added yet</p>
                    )}
                    
                    {/* Add New Note */}
                    {caseIsActive && isAuthorized && (
                      <div className="border-t pt-3 space-y-2">
                        <div className="flex gap-2">
                          <Select value={newNoteType} onValueChange={setNewNoteType}>
                            <SelectTrigger className="h-7 text-[10px] w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general" className="text-xs">General</SelectItem>
                              <SelectItem value="inspection" className="text-xs">Inspection</SelectItem>
                              <SelectItem value="customer_feedback" className="text-xs">Customer Feedback</SelectItem>
                              <SelectItem value="internal" className="text-xs">Internal</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Add a note..."
                            value={newNote}
                            onChange={e => setNewNote(e.target.value)}
                            className="h-7 text-xs flex-1"
                            data-testid="mrb-new-note-input"
                          />
                          <Button 
                            size="sm" 
                            className="h-7 text-xs"
                            onClick={handleAddNote}
                            disabled={addingNote || !newNote.trim()}
                            data-testid="mrb-add-note-btn"
                          >
                            {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* =============== ATTACHMENTS / PHOTOS =============== */}
                <Card>
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                      <Paperclip className="w-4 h-4 text-amber-600" />
                      Attachments & Photos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 space-y-3">
                    <MrbPhotoGallery caseId={selectedCase.id} attachments={selectedCase.attachments} />
                    {/* Existing Attachments */}
                    {(selectedCase.attachments || []).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedCase.attachments.map((att) => (
                          <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                            {att.content_type?.startsWith('image/') ? (
                              <Image className="w-4 h-4 text-green-600 flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{att.filename}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {att.description || att.attachment_type} • {att.uploaded_by_name}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDownloadAttachment(att.id, att.filename)}>
                                <Download className="w-3 h-3" />
                              </Button>
                              {(att.uploaded_by === user?.id || user?.role === 'admin') && caseIsActive && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:bg-red-50" onClick={() => handleDeleteAttachment(att.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No attachments yet</p>
                    )}
                    
                    {/* Upload New Attachment */}
                    {caseIsActive && isAuthorized && (
                      <div className="border-t pt-3 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Select value={attachmentType} onValueChange={setAttachmentType}>
                            <SelectTrigger className="h-7 text-[10px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="document" className="text-xs">Document</SelectItem>
                              <SelectItem value="photo" className="text-xs">Photo</SelectItem>
                              <SelectItem value="report" className="text-xs">Report</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            key={(selectedCase.attachments || []).length}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx"
                            onChange={e => setAttachmentFile(e.target.files?.[0] || null)}
                            className="h-7 text-[10px] col-span-2"
                            data-testid="mrb-attachment-file"
                          />
                          <Input
                            placeholder="Description..."
                            value={attachmentDesc}
                            onChange={e => setAttachmentDesc(e.target.value)}
                            className="h-7 text-xs"
                            data-testid="mrb-attachment-desc"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={handleUploadAttachment}
                          disabled={uploadingAttachment || !attachmentFile}
                          data-testid="mrb-upload-attachment-btn"
                        >
                          {uploadingAttachment ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileUp className="w-3 h-3 mr-1" />}
                          Upload Attachment
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Approvals section (for re-accept) */}
                {(selectedCase.disposition === 'reaccept' || selectedCase.status === 'in_review') && (
                  <Card>
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <UserCheck className="w-4 h-4 text-blue-600" />
                        Re-accept Approvals
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['program_mgmt', 'quality', 'inventory'].map(role => {
                          const approval = selectedCase.approvals?.[role] || {};
                          return (
                            <div key={role} className={`p-2.5 rounded border ${approval.approved ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                {approval.approved ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                                )}
                                <span className="text-[10px] font-semibold uppercase tracking-wide">{ROLE_LABELS[role]}</span>
                              </div>
                              {approval.approved ? (
                                <div>
                                  <p className="text-xs text-emerald-700">{approval.user_name}</p>
                                  <p className="text-[10px] text-emerald-600">{new Date(approval.date).toLocaleString()}</p>
                                  {approval.remarks && <p className="text-[10px] text-muted-foreground mt-0.5">{approval.remarks}</p>}
                                </div>
                              ) : (
                                <p className="text-[10px] text-gray-400">Pending</p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Customer acceptance for customer material */}
                      {selectedCase.is_customer_material && (
                        <div className={`mt-3 p-2.5 rounded border ${selectedCase.customer_acceptance_file_id ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5" />
                              <span className="text-[10px] font-semibold uppercase tracking-wide">Customer Acceptance</span>
                            </div>
                            <div className="flex gap-1">
                              {selectedCase.customer_acceptance_file_id ? (
                                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleDownloadFile} data-testid="mrb-download-acceptance">
                                  <Download className="w-3 h-3 mr-1" /> {selectedCase.customer_acceptance_filename}
                                </Button>
                              ) : caseIsActive && isAuthorized && userRoles.includes('program_mgmt') ? (
                                <label className="cursor-pointer">
                                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} data-testid="mrb-upload-acceptance-input" />
                                  <Button size="sm" variant="outline" className="h-6 text-[10px]" asChild disabled={uploading}>
                                    <span>
                                      {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileUp className="w-3 h-3 mr-1" />}
                                      Upload PDF
                                    </span>
                                  </Button>
                                </label>
                              ) : (
                                <span className="text-[10px] text-amber-600">Awaiting upload by Program Mgmt</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                {caseIsActive && isAuthorized && (
                  <Card className="border-blue-200">
                    <CardContent className="p-3">
                      <p className="text-[10px] font-semibold text-blue-700 mb-2 uppercase tracking-wide">Actions</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCase.disposition === 'pending' && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => openDisposition('reaccept')} data-testid="mrb-btn-reaccept">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Re-accept
                            </Button>
                            <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700" onClick={() => openDisposition('rework')} data-testid="mrb-btn-rework">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rework
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => openDisposition('reject')} data-testid="mrb-btn-reject">
                              <Ban className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {isReacceptReview && availableApprovalRoles.length > 0 && (
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={openApproval} data-testid="mrb-btn-approve">
                            <UserCheck className="w-3.5 h-3.5 mr-1" /> Approve Re-accept
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* History */}
                <Card>
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm font-semibold">Case History</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="space-y-2">
                      {(selectedCase.history || []).slice().reverse().map((h, idx) => (
                        <div key={idx} className="flex gap-2 text-xs border-b last:border-0 pb-2 last:pb-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">{h.user_name}</p>
                            <p className="text-muted-foreground">{h.details}</p>
                            <p className="text-[10px] text-muted-foreground">{new Date(h.date).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ===================== DISPOSITION DIALOG ===================== */}
      <Dialog open={dispOpen} onOpenChange={setDispOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dispType === 'reaccept' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
              {dispType === 'rework' && <RotateCcw className="w-5 h-5 text-orange-600" />}
              {dispType === 'reject' && <XCircle className="w-5 h-5 text-red-600" />}
              {dispType === 'reaccept' ? 'Re-accept' : dispType === 'rework' ? 'Rework' : 'Reject'} Disposition
            </DialogTitle>
            <DialogDescription>
              {dispType === 'reaccept' && 'This will start a multi-approval process. All 3 managers must approve.'}
              {dispType === 'rework' && 'Mark for rework. A new work order should be created.'}
              {dispType === 'reject' && 'Stock will be moved to the Rejection Area.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input
                type="number"
                value={dispQty}
                onChange={e => setDispQty(e.target.value)}
                className="h-8 text-xs"
                data-testid="mrb-disp-qty"
              />
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea
                value={dispRemarks}
                onChange={e => setDispRemarks(e.target.value)}
                placeholder="Reason for this disposition..."
                className="text-xs h-20"
                data-testid="mrb-disp-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleDisposition}
              disabled={submitting}
              size="sm"
              className={dispType === 'reject' ? 'bg-red-600 hover:bg-red-700' : dispType === 'rework' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="mrb-disp-submit"
            >
              {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Confirm {dispType === 'reaccept' ? 'Re-accept' : dispType === 'rework' ? 'Rework' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== APPROVAL DIALOG ===================== */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Approve Re-acceptance
            </DialogTitle>
            <DialogDescription>
              Select which role you are approving as. Each role must be approved by a different person.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Approval Role</Label>
              <Select value={approveRole} onValueChange={setApproveRole}>
                <SelectTrigger className="h-8 text-xs" data-testid="mrb-approve-role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {availableApprovalRoles.map(role => (
                    <SelectItem key={role} value={role} className="text-xs">
                      {ROLE_LABELS[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea
                value={approveRemarks}
                onChange={e => setApproveRemarks(e.target.value)}
                placeholder="Optional remarks..."
                className="text-xs h-16"
                data-testid="mrb-approve-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleApproval}
              disabled={submitting || !approveRole}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="mrb-approve-submit"
            >
              {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== SEND TO CUSTOMER DIALOG ===================== */}
      <Dialog open={sendToCustomerOpen} onOpenChange={setSendToCustomerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Send to Customer for Disposition
            </DialogTitle>
            <DialogDescription>
              Send MRB case details to the customer requesting their disposition decision.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCase && (
            <div className="space-y-4">
              {/* Summary Info */}
              <Card className="bg-purple-50/30 border-purple-200">
                <CardContent className="p-3 space-y-1">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Customer DC#:</span>{' '}
                      <span className="font-mono font-medium">{selectedCase.customer_challan_number || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Customer WO#:</span>{' '}
                      <span className="font-mono font-medium">{selectedCase.customer_wo_number || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Part:</span>{' '}
                      <span className="font-medium">{selectedCase.item_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Part No:</span>{' '}
                      <span className="font-mono">{selectedCase.item_unique_id}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Qty:</span>{' '}
                      <span className="font-medium">{selectedCase.quantity}</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t mt-2">
                    <span className="text-muted-foreground text-xs">Reason:</span>
                    <p className="text-xs font-medium">{selectedCase.qc_remarks || 'Not specified'}</p>
                  </div>
                </CardContent>
              </Card>
              
              <div>
                <Label className="text-xs">Customer Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="h-8 text-xs mt-1"
                  data-testid="mrb-customer-email"
                />
              </div>
              
              <div>
                <Label className="text-xs">CC Emails (comma separated)</Label>
                <Input
                  value={customerCc}
                  onChange={e => setCustomerCc(e.target.value)}
                  placeholder="cc1@example.com, cc2@example.com"
                  className="h-8 text-xs mt-1"
                  data-testid="mrb-customer-cc"
                />
              </div>
              
              <div>
                <Label className="text-xs">Additional Message (optional)</Label>
                <Textarea
                  value={customerMessage}
                  onChange={e => setCustomerMessage(e.target.value)}
                  placeholder="Any additional information for the customer..."
                  className="text-xs h-16 mt-1"
                  data-testid="mrb-customer-message"
                />
              </div>
              
              {/* Include Attachments */}
              {(selectedCase?.attachments || []).length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                  <Checkbox
                    id="include-attachments"
                    checked={includeAttachments}
                    onCheckedChange={setIncludeAttachments}
                    data-testid="mrb-include-attachments"
                  />
                  <Label htmlFor="include-attachments" className="text-xs cursor-pointer">
                    Include {selectedCase.attachments.length} attachment(s) in email
                  </Label>
                </div>
              )}
              
              {/* Notes Preview */}
              {(selectedCase?.notes || []).length > 0 && (
                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-[10px] text-blue-700 font-medium mb-1">
                    {selectedCase.notes.length} note(s) will be included in the email
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendToCustomerOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleSendToCustomer}
              disabled={sendingToCustomer || !customerEmail.trim()}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="mrb-send-customer-submit"
            >
              {sendingToCustomer && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Send to Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== CUSTOMER DISPOSITION DIALOG ===================== */}
      <Dialog open={custDispOpen} onOpenChange={setCustDispOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Record Customer Disposition
            </DialogTitle>
            <DialogDescription>
              Record the customer's decision for this MRB case. Optionally attach their reply document.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Customer Decision <span className="text-red-500">*</span></Label>
              <Select value={custDispType} onValueChange={setCustDispType}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="mrb-cust-disp-type">
                  <SelectValue placeholder="Select customer's disposition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="use_as_is" className="text-xs">Use As Is - Accept despite issue</SelectItem>
                  <SelectItem value="rework" className="text-xs">Rework - Customer provided instructions</SelectItem>
                  <SelectItem value="return" className="text-xs">Return - Send back to customer</SelectItem>
                  <SelectItem value="scrap" className="text-xs">Scrap - Dispose the material</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs">Customer's Remarks</Label>
              <Textarea
                value={custDispRemarks}
                onChange={e => setCustDispRemarks(e.target.value)}
                placeholder="Customer's instructions or remarks..."
                className="text-xs h-16 mt-1"
                data-testid="mrb-cust-disp-remarks"
              />
            </div>
            
            <div>
              <Label className="text-xs">Attach Customer's Reply Document (optional)</Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={e => setCustDispFile(e.target.files?.[0] || null)}
                className="h-8 text-xs mt-1"
                data-testid="mrb-cust-disp-file"
              />
              {custDispFile && (
                <p className="text-[10px] text-muted-foreground mt-1">Selected: {custDispFile.name}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustDispOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleRecordCustomerDisposition}
              disabled={recordingCustDisp || !custDispType}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="mrb-cust-disp-submit"
            >
              {recordingCustDisp && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Record Disposition
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: Repair Availability Dialog */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-lg" data-testid="mrb-repair-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Wrench className="w-4 h-4" /> Repair Availability
            </DialogTitle>
            <DialogDescription className="text-xs">
              Manually restore stock to an inbound challan line's OK quantity so it becomes
              available for Work Orders. Reduces quarantined quantity and reopens the challan if closed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Search challans */}
            <div>
              <Label className="text-xs">Search inbound challan</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Challan #, item, supplier..."
                  value={repairSearch}
                  onChange={e => setRepairSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') loadRepairChallans(); }}
                  className="h-8 text-xs"
                  data-testid="mrb-repair-search"
                />
                <Button variant="outline" size="sm" className="h-8" onClick={loadRepairChallans} disabled={repairLoading} data-testid="mrb-repair-search-btn">
                  {repairLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            {/* Select challan */}
            <div>
              <Label className="text-xs">Inbound challan</Label>
              <Select value={repairChallanId} onValueChange={v => { setRepairChallanId(v); setRepairLineIndex(''); setRepairQty(''); }}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="mrb-repair-challan-select">
                  <SelectValue placeholder={repairChallans.length ? 'Select challan' : 'No challans found'} />
                </SelectTrigger>
                <SelectContent>
                  {repairChallans.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">
                      {c.challan_number}{c.customer_challan_number ? ` / ${c.customer_challan_number}` : ''} — {c.supplier_name || 'N/A'} ({c.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Select line item */}
            {selectedRepairChallan && (
              <div>
                <Label className="text-xs">Line item</Label>
                <Select value={String(repairLineIndex)} onValueChange={v => { setRepairLineIndex(v); setRepairQty(''); }}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="mrb-repair-line-select">
                    <SelectValue placeholder="Select line item" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedRepairChallan.items.map(li => (
                      <SelectItem key={li.line_index} value={String(li.line_index)} className="text-xs">
                        #{li.line_index + 1} {li.item_name} (OK: {li.ok_quantity}, Quar: {li.quarantined_quantity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Action mode */}
            {selectedRepairLine && (
              <div>
                <Label className="text-xs">Action</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Button
                    type="button"
                    variant={repairMode === 'add' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-8 text-xs ${repairMode === 'add' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => { setRepairMode('add'); setRepairQty(''); }}
                    data-testid="mrb-repair-mode-add"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add to OK
                  </Button>
                  <Button
                    type="button"
                    variant={repairMode === 'reduce' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-8 text-xs ${repairMode === 'reduce' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    onClick={() => { setRepairMode('reduce'); setRepairQty(''); }}
                    data-testid="mrb-repair-mode-reduce"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reduce / Undo
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {repairMode === 'add'
                    ? 'Restores availability: increases OK qty, decreases quarantined, reopens challan if closed.'
                    : 'Undoes a mistaken repair: decreases OK qty on this line. Quarantined qty is left unchanged.'}
                </p>
              </div>
            )}

            {/* Current quantities */}
            {selectedRepairLine && (
              <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded border text-center">
                <div>
                  <p className="text-[10px] text-muted-foreground">Ordered</p>
                  <p className="text-sm font-semibold">{selectedRepairLine.ordered_quantity}</p>
                </div>
                <div>
                  <p className="text-[10px] text-emerald-600">OK Qty</p>
                  <p className="text-sm font-semibold text-emerald-700" data-testid="mrb-repair-current-ok">{selectedRepairLine.ok_quantity}</p>
                </div>
                <div>
                  <p className="text-[10px] text-amber-600">Quarantined</p>
                  <p className="text-sm font-semibold text-amber-700" data-testid="mrb-repair-current-quar">{selectedRepairLine.quarantined_quantity}</p>
                </div>
              </div>
            )}

            {/* Quantity */}
            {selectedRepairLine && (
              <div>
                <Label className="text-xs">{repairMode === 'add' ? 'Quantity to add back to OK' : 'Quantity to remove from OK'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Enter quantity"
                  value={repairQty}
                  onChange={e => setRepairQty(e.target.value)}
                  className="h-8 text-xs mt-1"
                  data-testid="mrb-repair-qty"
                />
                {repairMode === 'add' && repairQty && parseFloat(repairQty) > (selectedRepairLine.quarantined_quantity || 0) && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mrb-repair-warning">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    <span>Quantity exceeds current quarantined qty ({selectedRepairLine.quarantined_quantity}). This will still be applied.</span>
                  </div>
                )}
                {repairMode === 'reduce' && repairQty && parseFloat(repairQty) > (selectedRepairLine.ok_quantity || 0) && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mrb-repair-warning">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    <span>Quantity exceeds current OK qty ({selectedRepairLine.ok_quantity}). OK qty will be set to 0.</span>
                  </div>
                )}
              </div>
            )}

            {/* Remarks */}
            {selectedRepairLine && (
              <div>
                <Label className="text-xs">Remarks (optional)</Label>
                <Textarea
                  placeholder="Reason for this repair..."
                  value={repairRemarks}
                  onChange={e => setRepairRemarks(e.target.value)}
                  className="text-xs mt-1"
                  rows={2}
                  data-testid="mrb-repair-remarks"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleRepairSubmit}
              disabled={repairSubmitting || !selectedRepairLine || !repairQty}
              size="sm"
              className={repairMode === 'reduce' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="mrb-repair-submit"
            >
              {repairSubmitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {repairMode === 'reduce' ? 'Remove from OK' : 'Restore Availability'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
